import type { CaptureKind } from '@mystiq/contracts';
import { useInfiniteQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router';
import { keys, useWorkspaceId, useWorkspaceInfo } from '../api/hooks.ts';
import { useApi } from '../auth/session.tsx';
import { RecordingPanel } from '../components/Recorder.tsx';
import { EmptyState, ItemRow } from '../components/ui.tsx';
import { Meeting, Mic } from '../icons.ts';

const PAGES: Record<CaptureKind, { title: string; intro: string; empty: string; icon: typeof Mic }> = {
  voice_note: {
    title: 'Voice notes',
    intro: 'Say what is on your mind. Mystiq keeps the recording, writes down what you said, and tidies it into a clean transcript; each is its own item.',
    empty: 'No voice notes yet',
    icon: Mic,
  },
  meeting: {
    title: 'Meetings',
    intro: 'Record a meeting or upload its recording. Mystiq keeps the recording, writes a transcript, and makes meeting notes with decisions and action items.',
    empty: 'No meetings yet',
    icon: Meeting,
  },
};

/** Voice notes or meetings: recordings made (or uploaded) for that purpose, newest first. */
function RecordingsPage({ capture }: { capture: CaptureKind }) {
  const ws = useWorkspaceId();
  const api = useApi();
  const navigate = useNavigate();
  const info = useWorkspaceInfo(ws);
  const page = PAGES[capture];
  const list = useInfiniteQuery({
    queryKey: keys.items(ws, { capture }),
    queryFn: ({ pageParam }) => api.items(ws, { capture, ...(pageParam ? { cursor: pageParam } : {}) }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.nextCursor ?? undefined,
  });
  const rows = list.data?.pages.flatMap((result) => result.items) ?? [];
  return (
    <div className="page">
      <h1 className="page-title">{page.title}</h1>
      <p className="meta" style={{ marginTop: 'var(--space-6)', maxWidth: 720 }}>
        {page.intro}
      </p>
      {info.data && info.data.role !== 'viewer' && (
        <div className="content">
          <RecordingPanel ws={ws} capture={capture} onSaved={(item) => navigate(`/w/${ws}/items/${item.id}`)} />
        </div>
      )}
      <div className="content stack">
        <ul className="rows">
          {rows.map((item) => (
            <ItemRow key={item.id} ws={ws} item={item} />
          ))}
        </ul>
        {list.hasNextPage && (
          <div>
            <button className="button" onClick={() => void list.fetchNextPage()}>
              Show more
            </button>
          </div>
        )}
        {list.isSuccess && !rows.length && <EmptyState icon={page.icon} title={page.empty} />}
      </div>
    </div>
  );
}

export const VoiceNotesPage = () => <RecordingsPage capture="voice_note" />;
export const MeetingsPage = () => <RecordingsPage capture="meeting" />;
